/**
 * use this file to register and export all API handler that will be used in the system
 **/

import type { InjectionKey } from 'vue';
import { createTokenApi } from './client-only/token-api';
import { createWalletApi } from './client-only/wallet-api';
import { createResponsibleGamingApi } from './client-only/responsible-gaming-api';
import { createTransactionsApi } from './client-only/transactions-api';
import { createCustomerServiceSearchApi } from './client-only/customer-service-search-api';
import { createProfileApi } from './client-only/profile-api';
import { createBankAccountApi } from './client-only/bank-account-api';
import { createEnrichmentLotteryApi } from './client-only/enrichment-lottery-api';
import { createTotoRetailApi } from './client-only/toto-retail-api';
import { createKioskApi } from '~/api/client-only/kiosk-api';
import { createContactPreferencesApi } from '~/api/client-only/contact-preferences-api';
import { createReturnUrlApi } from '~/api/client-only/return-url-api';
import { createUnsubscribeApi } from '~/api/client-only/unsubscribe-api';
import { createRegistrationApi } from '~/api/client-only/registration-api';

export const walletApiKey: InjectionKey<ReturnType<typeof createWalletApi>> = Symbol('WalletApi');
export const responsibleGamingApiKey: InjectionKey<ReturnType<typeof createResponsibleGamingApi>> = Symbol('ResponsibleGamingApi');
export const transactionsApiKey: InjectionKey<ReturnType<typeof createTransactionsApi>> = Symbol('TransactionsApi');
export const customerServiceSearchApiKey: InjectionKey<ReturnType<typeof createCustomerServiceSearchApi>> =
	Symbol('CustomerServiceSearchApi');
export const profileApiKey: InjectionKey<ReturnType<typeof createProfileApi>> = Symbol('ProfileApi');
export const bankAccountApiKey: InjectionKey<ReturnType<typeof createBankAccountApi>> = Symbol('BankAccountApi');
export const enrichmentLotteryApiKey: InjectionKey<ReturnType<typeof createEnrichmentLotteryApi>> = Symbol('createEnrichmentLotteryApi');
export const totoRetailApiKey: InjectionKey<ReturnType<typeof createTotoRetailApi>> = Symbol('TotoRetailApi');
export const kioskApiKey: InjectionKey<ReturnType<typeof createKioskApi>> = Symbol('KioskApi');
export const contactPreferencesApiKey: InjectionKey<ReturnType<typeof createContactPreferencesApi>> = Symbol('ContactPreferencesApi');
export const returnUrlApiKey: InjectionKey<ReturnType<typeof createReturnUrlApi>> = Symbol('ReturnUrlApi');
export const unsubscribeApiKey: InjectionKey<ReturnType<typeof createUnsubscribeApi>> = Symbol('UnsubscribeApi');
export const registrationApiKey: InjectionKey<ReturnType<typeof createRegistrationApi>> = Symbol('RegistrationApi');

/**
 * Provides api's to be used within pages of the application.
 * Note; This hook does not provide the cmsPageApi, because that should only be used on page-level.
 * @param baseUrl Base url of the api we are calling (portal backend)
 * @param pageId The cms page-id used for providing the header in all api calls
 */
export default function useClientApi(baseUrl: string, pageId?: string) {
	const nuxtApp = useNuxtApp();
	const customHeaders = pageId ? { 'Cms-Page-Id': pageId } : undefined;

	const { getXSRFToken: tokenApiCall } = createTokenApi(getNloFetcherConfig({ baseUrl, customHeaders, errorParser: getPortalError }));
	const fetchConfig = getNloFetcherConfig({ baseUrl, tokenApiCall, customHeaders, errorParser: getPortalError });

	nuxtApp.vueApp.provide(walletApiKey, createWalletApi(fetchConfig));
	nuxtApp.vueApp.provide(responsibleGamingApiKey, createResponsibleGamingApi(fetchConfig));
	nuxtApp.vueApp.provide(transactionsApiKey, createTransactionsApi(fetchConfig));
	nuxtApp.vueApp.provide(customerServiceSearchApiKey, createCustomerServiceSearchApi(fetchConfig));
	nuxtApp.vueApp.provide(profileApiKey, createProfileApi(fetchConfig));
	nuxtApp.vueApp.provide(bankAccountApiKey, createBankAccountApi(fetchConfig));
	nuxtApp.vueApp.provide(enrichmentLotteryApiKey, createEnrichmentLotteryApi(fetchConfig));
	nuxtApp.vueApp.provide(totoRetailApiKey, createTotoRetailApi(fetchConfig));
	nuxtApp.vueApp.provide(kioskApiKey, createKioskApi(fetchConfig));
	nuxtApp.vueApp.provide(contactPreferencesApiKey, createContactPreferencesApi(fetchConfig));
	nuxtApp.vueApp.provide(returnUrlApiKey, createReturnUrlApi(fetchConfig));
	nuxtApp.vueApp.provide(unsubscribeApiKey, createUnsubscribeApi(fetchConfig));
	nuxtApp.vueApp.provide(registrationApiKey, createRegistrationApi(fetchConfig));
}

export function useWalletApi() {
	const walletApi = inject(walletApiKey);

	if (!walletApi) {
		throw new Error('Wallet api not provided');
	}

	return walletApi;
}

export function useTransactionsApi() {
	const transactionsApi = inject(transactionsApiKey);

	if (!transactionsApi) {
		throw new Error('Transaction api not provided');
	}

	return transactionsApi;
}

/**
 * Returns injected customer service search api.
 * If there is no api provided, an error will be thrown.
 */
export function useCustomerServiceSearchApi() {
	const customerServiceSearchApi = inject(customerServiceSearchApiKey);

	if (!customerServiceSearchApi) {
		throw new Error('Customer service search api not provided');
	}

	return customerServiceSearchApi;
}

/**
 * Returns injected responsible gaming api.
 * If there is no api provided, an error will be thrown.
 */
export function useResponsibleGamingApi() {
	const responsibleGamingApi = inject(responsibleGamingApiKey);

	if (!responsibleGamingApi) {
		throw new Error('Responsible Gaming api not provided');
	}

	return responsibleGamingApi;
}

/**
 * Returns injected profile api.
 * If there is no api provided, an error will be thrown.
 */
export function useProfileApi() {
	const profileApi = inject(profileApiKey);

	if (!profileApi) {
		throw new Error('Profile api not provided');
	}

	return profileApi;
}

export function useBankAccountApi() {
	const bankAccountApi = inject(bankAccountApiKey);
	if (!bankAccountApi) {
		throw new Error('Bank account api not provided');
	}

	return bankAccountApi;
}

export function useEnrichmentLotteryApi() {
	const api = inject(enrichmentLotteryApiKey);
	if (!api) {
		throw new Error('Enrichment lottery api not provided');
	}

	return api;
}

export function useTotoRetailApi() {
	const totoRetailApi = inject(totoRetailApiKey);
	if (!totoRetailApi) {
		throw new Error('Toto retail api not provided');
	}

	return totoRetailApi;
}

export function useKioskApi() {
	const kioskApi = inject(kioskApiKey);

	if (!kioskApi) {
		throw new Error('Kiosk api not provided');
	}

	return kioskApi;
}

export function useContactPreferencesApi() {
	const contactPreferencesApi = inject(contactPreferencesApiKey);

	if (!contactPreferencesApi) {
		throw new Error('Contact preferences api not provided');
	}

	return contactPreferencesApi;
}

export function useReturnUrlApi() {
	const returnUrlApi = inject(returnUrlApiKey);

	if (!returnUrlApi) {
		throw new Error('Return url api not provided');
	}

	return returnUrlApi;
}

export function useUnsubscribeApi() {
	const unsubscribeApi = inject(unsubscribeApiKey);

	if (!unsubscribeApi) {
		throw new Error('Unsubscribe api not provided');
	}

	return unsubscribeApi;
}

export function useRegistrationApi() {
	const registrationApi = inject(registrationApiKey);

	if (!registrationApi) {
		throw new Error('Registration api not provided');
	}

	return registrationApi;
}
